// src/components/books/BookCard.tsx
// Full redesign: horizontal card with cover | metadata+stats | scrollable unit strip.
// Prop interface is a superset of the old one — HomePage stays compatible.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore }    from '../../stores/useAuthStore';
import { usePaymentStore } from '../../stores/usePaymentStore';
import { useExerciseStore } from '../../stores/useExerciseStore';
import { progressSync } from '../../services/progressSync';
import { useTestStore }    from '../../stores/useTestStore';
import { useSubscription } from '../../hooks/useSubscription';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore }   from '../../stores/useThemeStore';
import { useT }            from '../../data/translations';
import { IconLock, IconLevel } from '../icons/Icons';
import type { AllProgress, UnitProgress } from '../../types/progress';

// ── Constants ─────────────────────────────────────────────────────────────────
const UNLOCK_THRESHOLD = 80; // percent

// ── Helpers ───────────────────────────────────────────────────────────────────
function unitPercent(progress: UnitProgress | undefined): number {
  if (!progress) return 0;
  const sections = Object.values(progress);
  if (sections.length === 0) return 0;
  return Math.round(sections.reduce((s, p) => s + (p.percentage ?? 0), 0) / sections.length);
}

function overallPercent(progressData: AllProgress, bookId: string, unitCount: number): number {
  if (unitCount === 0) return 0;
  let total = 0;
  for (let i = 1; i <= unitCount; i++) total += unitPercent(progressData[`${bookId}_unit${i}`]);
  return Math.round(total / unitCount);
}

function getLastActivityMs(progressData: AllProgress, bookId: string, unitCount: number): number | null {
  let latest: number | null = null;
  for (let i = 1; i <= unitCount; i++) {
    const up = progressData[`${bookId}_unit${i}`];
    if (!up) continue;
    for (const sp of Object.values(up)) {
      if (sp.lastUpdatedMs && (latest === null || sp.lastUpdatedMs > latest)) {
        latest = sp.lastUpdatedMs;
      }
    }
  }
  return latest;
}

function formatActivity(ms: number): string {
  const diff = Math.max(0, Date.now() - ms);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ── Lock state ────────────────────────────────────────────────────────────────
type LockState = 'open' | 'sub_locked' | 'progress_locked';

// ── Progress Ring ─────────────────────────────────────────────────────────────
interface MiniRingProps {
  percent:   number;
  size?:     number;
  isActive?: boolean;
  uid:       string;
}

function MiniRing({ percent, size = 52, isActive = false, uid }: MiniRingProps) {
  const C = useThemePalette();
  const sw = 3.5;
  const r  = (size - sw) / 2;
  const c  = size / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (Math.min(percent, 100) / 100) * circumference;
  const gradId = `bc-ring-${uid}`;
  const isComplete = percent >= 100;
  const strokeColor = isActive ? `url(#${gradId})` : isComplete ? C.success : C.muted;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', flexShrink: 0 }}>
      {isActive && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor={C.accent}  />
            <stop offset="100%" stopColor={C.accent2} />
          </linearGradient>
        </defs>
      )}
      <circle cx={c} cy={c} r={r} fill="none" stroke={C.border} strokeWidth={sw} />
      {percent > 0 && (
        <circle
          cx={c} cy={c} r={r} fill="none"
          stroke={strokeColor}
          strokeWidth={sw} strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
        />
      )}
      <text
        x={c} y={c}
        textAnchor="middle" dominantBaseline="central"
        fill={isActive ? C.text : isComplete ? C.success : C.muted}
        fontSize={isComplete ? '9' : '10'}
        fontWeight="800"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {isComplete ? '✓' : `${percent}%`}
      </text>
    </svg>
  );
}

// ── Unit Tile ─────────────────────────────────────────────────────────────────
interface UnitTileProps {
  bookId:        string;
  unitNumber:    number;
  totalUnits:    number;
  unitTitle?:    string;
  percent:       number;
  lockState:     LockState;
  isActive:      boolean;
  isLoading:     boolean;
  onStart:       () => void;
  onStartTest:   (version: string) => void;
  unitProgress?: UnitProgress;
}

const ALL_TEST_VERSIONS = [
  { labelKey: 'TEST_A',        version: 'A'        },
  { labelKey: 'TEST_B',        version: 'B'        },
  { labelKey: 'TEST_PROGRESS', version: 'progress' },
  { labelKey: 'TEST_FINAL',    version: 'final'    },
] as const;

function getTestVersions(
  unitNumber: number,
  totalUnits: number,
): ReadonlyArray<{ labelKey: string; version: string }> {
  const middle = Math.ceil(totalUnits / 2);
  const last   = totalUnits;
  if (unitNumber === last)   return ALL_TEST_VERSIONS;
  if (unitNumber === middle) return ALL_TEST_VERSIONS.slice(0, 3);
  return ALL_TEST_VERSIONS.slice(0, 2);
}

function UnitTile({
  bookId, unitNumber, totalUnits, unitTitle, percent, lockState, isActive, isLoading, onStart, onStartTest, unitProgress,
}: UnitTileProps) {
  const C      = useThemePalette();
  const isDark = useThemeStore((s) => s.theme) === 'dark';
  const t      = useT();

  const [hover,    setHover]    = useState(false);
  const [btnHover, setBtnHover] = useState(false);

  const locked     = lockState !== 'open';
  const isReview   = !locked && percent >= 100;
  const isContinue = !locked && percent > 0 && percent < 100;

  const btnLabel = locked    ? t('UNIT_LOCKED')
    : isReview               ? t('UNIT_REVIEW')
    : isContinue             ? t('UNIT_CONTINUE')
    : t('UNIT_START');

  const handleClick = () => { if (!locked && !isLoading) onStart(); };
  const handleKey   = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !locked) { e.preventDefault(); onStart(); }
  };

  return (
    <div
      role={locked ? 'article' : 'button'}
      tabIndex={locked ? -1 : 0}
      aria-label={locked ? `Unit ${unitNumber} — locked` : `${btnLabel} Unit ${unitNumber}${unitTitle ? ': ' + unitTitle : ''}`}
      aria-disabled={locked || undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleClick}
      onKeyDown={handleKey}
      style={{
        width:         '120px',
        minWidth:      '120px',
        borderRadius:  '14px',
        padding:       '12px 8px 10px',
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        gap:           '7px',
        cursor:        locked ? 'default' : 'pointer',
        transition:    'all 0.2s ease',
        userSelect:    'none',
        position:      'relative',
        background: isActive
          ? 'rgba(79,140,247,0.07)'
          : hover && !locked
            ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
            : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
        border: isActive
          ? '1px solid rgba(79,140,247,0.6)'
          : hover && !locked
            ? `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(79,140,247,0.25)'}`
            : `1px solid ${C.border}`,
        boxShadow: isActive
          ? `0 0 18px rgba(79,140,247,0.18), inset 0 1px 0 rgba(79,140,247,0.1)`
          : 'none',
        transform: (hover && !locked) ? 'translateY(-2px)' : 'none',
        outline: 'none',
      }}
    >
      {/* Focus ring */}
      <style>{`
        [data-bc-tile="${bookId}-${unitNumber}"]:focus-visible {
          box-shadow: 0 0 0 2px #4f8cf7 !important;
        }
      `}</style>
      <span data-bc-tile={`${bookId}-${unitNumber}`} style={{ display: 'none' }} />

      {/* Unit label */}
      <span style={{
        fontSize:      '0.58rem',
        fontWeight:    700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color:         isActive ? C.accent : locked ? `${C.muted}80` : C.muted,
      }}>
        Unit {unitNumber}
      </span>

      {/* Unit topic name — 2 line max */}
      {unitTitle && (
        <span style={{
          fontSize:             '0.66rem',
          fontWeight:           600,
          color:                locked ? `${C.text}50` : C.text,
          textAlign:            'center',
          lineHeight:           1.35,
          minHeight:            '28px',
          display:              '-webkit-box',
          WebkitLineClamp:      2,
          WebkitBoxOrient:      'vertical',
          overflow:             'hidden',
          textOverflow:         'ellipsis',
        } as React.CSSProperties}>
          {unitTitle}
        </span>
      )}

      {/* Ring or lock icon */}
      {locked ? (
        <div style={{
          width:          '52px',
          height:         '52px',
          borderRadius:   '50%',
          background:     isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)',
          border:         `1px solid ${C.border}`,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          color:          `${C.muted}66`,
          flexShrink:     0,
        }}>
          <IconLock size={18} />
        </div>
      ) : (
        <MiniRing
          percent={percent}
          size={52}
          isActive={isActive}
          uid={`${bookId}-u${unitNumber}`}
        />
      )}

      {/* Action */}
      {isLoading ? (
        <div style={{
          width:        '16px',
          height:       '16px',
          borderRadius: '50%',
          border:       `2px solid ${C.border}`,
          borderTopColor: C.accent,
          animation:    'esp-spin 0.7s linear infinite',
        }} />
      ) : (
        <span
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          style={{
            display:       'inline-flex',
            alignItems:    'center',
            gap:           '4px',
            padding:       '4px 10px',
            borderRadius:  '999px',
            fontSize:      '0.6rem',
            fontWeight:    700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            fontFamily:    'inherit',
            pointerEvents: locked ? 'none' : 'auto',
            cursor:        locked ? 'default' : 'pointer',
            background:    (isContinue || isReview)
              ? `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`
              : locked
                ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)')
                : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'),
            color:         (isContinue || isReview) ? '#fff' : locked ? `${C.muted}80` : C.text,
            border:        locked ? `1px solid ${C.border}` : 'none',
            boxShadow:     (isContinue && isActive)
              ? `0 4px 14px rgba(79,140,247,0.45)`
              : (!locked && btnHover)
                ? `0 6px 18px rgba(79,140,247,0.4)`
                : 'none',
            transform:     (!locked && btnHover) ? 'translateY(-1px)' : 'none',
            transition:    'transform 0.18s ease, box-shadow 0.18s ease',
          }}
        >
          {locked && <IconLock size={7} color="currentColor" />}
          {btnLabel}
        </span>
      )}

      {/* ── Test pills ────────────────────────────────────────────────────────── */}
      <div
        style={{
          display:        'flex',
          flexWrap:       'wrap',
          gap:            '3px',
          justifyContent: 'center',
          width:          '100%',
          marginTop:      '2px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {getTestVersions(unitNumber, totalUnits).map(({ labelKey, version }) => (
          <TestPill
            key={version}
            label={t(labelKey as any)}
            locked={locked}
            version={version}
            unitProgress={unitProgress}
            bookId={bookId}
            unitNumber={unitNumber}
            onClick={() => onStartTest(version)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Test Pill ─────────────────────────────────────────────────────────────────
function TestPill({
  label, locked, version, unitProgress, bookId, unitNumber, onClick,
}: {
  label:          string;
  locked:         boolean;
  version:        string;
  unitProgress?:  UnitProgress;
  bookId:         string;
  unitNumber:     number;
  onClick:        () => void;
}) {
  const C      = useThemePalette();
  const isDark = useThemeStore((s) => s.theme) === 'dark';
  const [hov,          setHov]          = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);

  // ── Test progress from store ───────────────────────────────────────────────
  const sectionKey = `test_${version}`;
  const sp         = unitProgress?.[sectionKey];
  const pct        = sp?.percentage ?? 0;
  const passed     = sp?.completedFlag === true;
  const attempted  = !!sp && (sp.maxScore > 0 || sp.percentage > 0);
  const failed     = attempted && !passed;

  const ringStroke  = passed ? '#34d399' : failed ? '#f87171' : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.10)');
  const ringTrack   = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';

  const tooltip = locked        ? 'Unlock to take this test'
    : passed                    ? `Score: ${Math.round(pct)}% — PASS`
    : failed                    ? `Score: ${Math.round(pct)}% — FAIL`
    : `Start ${label}`;

  // ── 20px inline ring SVG ───────────────────────────────────────────────────
  const RING = 20;
  const sw   = 2.2;
  const r    = (RING - sw) / 2;
  const cc   = RING / 2;
  const circ = 2 * Math.PI * r;
  const off  = circ - (Math.min(pct, 100) / 100) * circ;

  // ── Reset handler (two-click confirmation) ─────────────────────────────────
  const handleReset = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!resetConfirm) {
      setResetConfirm(true);
      // Auto-cancel after 3s if no second click
      setTimeout(() => setResetConfirm(false), 3000);
      return;
    }
    setResetConfirm(false);
    const scopedKey = `${bookId}_unit${unitNumber}`;
    try {
      await progressSync.saveProgress(scopedKey, sectionKey, {
        completed: 0, total: 0, score: 0, maxScore: 0, percentage: 0,
        completedFlag: false,
        lastUpdated: new Date().toISOString(),
        lastUpdatedMs: Date.now(),
        resetAt: Date.now(),
      }, { force: true });
      const prog = await progressSync.loadProgress(scopedKey);
      useExerciseStore.setState((s) => ({
        progressData: { ...s.progressData, [scopedKey]: prog || {} },
      }));
    } catch (err) {
      console.warn('[TestPill] reset failed:', err);
    }
  };

  // ── Derived pill colours ───────────────────────────────────────────────────
  const pillBg     = hov
    ? locked ? 'rgba(168,85,247,0.15)' : passed ? 'rgba(52,211,153,0.14)' : failed ? 'rgba(248,113,113,0.14)' : 'rgba(79,140,247,0.18)'
    : passed  ? (isDark ? 'rgba(52,211,153,0.06)' : 'rgba(52,211,153,0.05)')
    : failed  ? (isDark ? 'rgba(248,113,113,0.06)' : 'rgba(248,113,113,0.05)')
    : (isDark  ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)');

  const pillBorder = hov
    ? `1px solid ${locked ? 'rgba(168,85,247,0.40)' : passed ? 'rgba(52,211,153,0.50)' : failed ? 'rgba(248,113,113,0.50)' : 'rgba(79,140,247,0.40)'}`
    : passed ? '1px solid rgba(52,211,153,0.28)'
    : failed  ? '1px solid rgba(248,113,113,0.28)'
    : `1px solid ${C.border}`;

  const pillColor  = hov
    ? (locked ? '#c084fc' : passed ? '#34d399' : failed ? '#f87171' : C.accent)
    : (passed ? '#34d399' : failed ? '#f87171' : C.muted);

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
      {/* Main pill button */}
      <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        title={tooltip}
        style={{
          padding:       '3px 6px',
          borderRadius:  '5px',
          background:    pillBg,
          border:        pillBorder,
          color:         pillColor,
          fontSize:      '0.55rem',
          fontWeight:    700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          cursor:        'pointer',
          fontFamily:    'inherit',
          transition:    'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
          display:       'inline-flex',
          alignItems:    'center',
          gap:           '4px',
          whiteSpace:    'nowrap',
        }}
      >
        {/* Lock icon or progress ring */}
        {locked ? (
          <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        ) : (
          <svg width={RING} height={RING} viewBox={`0 0 ${RING} ${RING}`} style={{ flexShrink: 0 }}>
            <circle cx={cc} cy={cc} r={r} fill="none" stroke={ringTrack} strokeWidth={sw} />
            {pct > 0 && (
              <circle cx={cc} cy={cc} r={r} fill="none" stroke={ringStroke} strokeWidth={sw}
                strokeLinecap="round"
                strokeDasharray={`${circ} ${circ}`}
                strokeDashoffset={off}
                style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
              />
            )}
            {attempted && (
              <text x={cc} y={cc} textAnchor="middle" dominantBaseline="central"
                fill={ringStroke} fontSize="5.5" fontWeight="800"
                fontFamily="system-ui, -apple-system, sans-serif"
              >
                {Math.round(pct)}%
              </text>
            )}
          </svg>
        )}
        {label}
      </button>

      {/* Reset button — always reserves space, visible only when there is progress */}
      <button
        onClick={handleReset}
        title={resetConfirm ? 'Click again to confirm reset' : 'Reset this test'}
        style={{
          padding:        '2px 4px',
          borderRadius:   '4px',
          background:     resetConfirm ? 'rgba(248,113,113,0.16)' : 'transparent',
          border:         resetConfirm ? '1px solid rgba(248,113,113,0.40)' : '1px solid transparent',
          color:          resetConfirm ? '#f87171' : C.muted,
          fontSize:       '0.7rem',
          lineHeight:     1,
          cursor:         (attempted && !locked) ? 'pointer' : 'default',
          fontFamily:     'inherit',
          display:        'inline-flex',
          alignItems:     'center',
          transition:     'all 0.15s ease',
          userSelect:     'none',
          visibility:     (attempted && !locked) ? 'visible' : 'hidden',
          pointerEvents:  (attempted && !locked) ? 'auto'    : 'none',
        }}
      >
        ↺
      </button>
    </div>
  );
}

// ── Public interface ──────────────────────────────────────────────────────────
export interface BookCardProps {
  bookId:       string;
  title:        string;
  description:  string;
  coverUrl:     string;
  unitCount:    number;
  level?:       string;
  tier?:        'free' | 'premium';
  unitTitles?:  string[];
}

// ── BookCard ──────────────────────────────────────────────────────────────────
export default function BookCard({
  bookId, title, description, coverUrl, unitCount, level, tier, unitTitles,
}: BookCardProps) {
  const C      = useThemePalette();
  const isDark = useThemeStore((s) => s.theme) === 'dark';

  const navigate         = useNavigate();
  const progressData     = useExerciseStore((s) => s.progressData);
  const startUnit        = useExerciseStore((s) => s.startUnit);
  const openAuthModal    = useAuthStore((s) => s.openAuthModal);
  const openPaymentModal = usePaymentStore((s) => s.openModal);
  const { hasAccess, isSubscribed } = useSubscription();

  const scrollRef  = useRef<HTMLDivElement>(null);
  const [imgError,  setImgError]  = useState(false);
  const [cardHover, setCardHover] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [isMobile,  setIsMobile]  = useState(
    () => typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn, { passive: true });
    return () => window.removeEventListener('resize', fn);
  }, []);

  // ── Per-unit data ──────────────────────────────────────────────────────────
  const units = Array.from({ length: unitCount }, (_, i) => {
    const n         = i + 1;
    const unitId    = `unit${n}`;
    const scopedKey = `${bookId}_${unitId}`;
    const pct       = unitPercent(progressData[scopedKey]);

    let lockState: LockState = 'open';
    if (!hasAccess(bookId, unitId)) {
      lockState = 'sub_locked';
    } else if (!isSubscribed && n > 1 && unitPercent(progressData[`${bookId}_unit${n - 1}`]) < UNLOCK_THRESHOLD) {
      lockState = 'progress_locked';
    }

    return { n, unitId, pct, lockState };
  });

  // Active unit
  const activeN = (() => {
    const inProg = units.find(u => u.lockState === 'open' && u.pct > 0 && u.pct < 100);
    if (inProg) return inProg.n;
    const unstarted = units.find(u => u.lockState === 'open' && u.pct === 0);
    if (unstarted) return unstarted.n;
    return 1;
  })();

  // Book-level stats
  const overallPct   = overallPercent(progressData, bookId, unitCount);
  const lastMs       = getLastActivityMs(progressData, bookId, unitCount);
  const isPremium    = tier === 'premium' || (tier == null && !isSubscribed && unitCount > 1);
  const tierLabel    = isPremium ? 'PREMIUM' : 'FREE';
  const tierColor    = isPremium ? '#c084fc' : C.success;
  const tierBg       = isPremium ? 'rgba(168,85,247,0.10)' : 'rgba(52,211,153,0.08)';
  const tierBorder   = isPremium ? 'rgba(168,85,247,0.30)' : 'rgba(52,211,153,0.30)';

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleStart = useCallback(async (unitId: string) => {
    if (loadingId) return;
    setLoadingId(unitId);
    const result = await startUnit(unitId, bookId);
    setLoadingId(null);
    if (result.ok) {
      navigate('/exercise');
    } else {
      if (result.reason === 'login_required') openAuthModal('login');
      else if (result.reason === 'access_denied') openPaymentModal();
    }
  }, [loadingId, startUnit, bookId, navigate, openAuthModal, openPaymentModal]);

  const handleStartTest = useCallback(async (
    unitNumber: number,
    lockState: LockState,
    version: string,
  ) => {
    if (lockState === 'sub_locked') {
      openPaymentModal();
      return;
    }
    const result = await useTestStore.getState().startTest(bookId, String(unitNumber), version);
    if (result.ok) {
      navigate('/test');
    } else if (result.reason === 'login_required') {
      openAuthModal('login');
    } else if (result.reason === 'access_denied') {
      openPaymentModal();
    }
  }, [bookId, navigate, openAuthModal, openPaymentModal]);

  const scrollUnits = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const tileW = 120 + 8;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -(tileW * 5) : (tileW * 5), behavior: 'smooth' });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        background:     isDark
          ? 'rgba(13,18,32,0.92)'
          : cardHover ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.95)',
        border:         `1px solid ${cardHover
          ? (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(79,140,247,0.22)')
          : C.border}`,
        borderRadius:   '16px',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: cardHover
          ? isDark
            ? '0 20px 52px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.07)'
            : '0 12px 32px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.9)'
          : isDark
            ? '0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)'
            : '0 2px 8px rgba(0,0,0,0.06)',
        transition:     'border-color 0.2s ease, box-shadow 0.2s ease',
        overflow:       'hidden',
      }}
      onMouseEnter={() => setCardHover(true)}
      onMouseLeave={() => setCardHover(false)}
    >
      <div style={{
        display:       'flex',
        flexDirection: isMobile ? 'column' : 'row',
        minHeight:     isMobile ? 'auto' : '220px',
      }}>

        {/* ── LEFT: Cover ───────────────────────────────────────────────── */}
        <div style={{
          flexShrink:  0,
          width:       isMobile ? '100%' : '180px',
          position:    'relative',
          overflow:    'hidden',
        }}>
          {coverUrl && !imgError ? (
            <img
              src={coverUrl}
              alt={`${title} cover`}
              style={{
                width:      '100%',
                height:     isMobile ? '180px' : '100%',
                objectFit:  'cover',
                display:    'block',
                minHeight:  isMobile ? 'auto' : '100%',
              }}
              onError={() => setImgError(true)}
            />
          ) : (
            <div style={{
              width:           '100%',
              height:          isMobile ? '160px' : '100%',
              minHeight:       isMobile ? 'auto' : '220px',
              background:      `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
          )}

          {/* Level overlay at bottom of cover */}
          {level && (
            <div style={{
              position:       'absolute',
              bottom:         '8px',
              left:           '6px',
              right:          '6px',
              padding:        '5px 8px',
              background:     'rgba(8,12,20,0.78)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderRadius:   '8px',
              textAlign:      'center',
            }}>
              <div style={{ fontWeight: 900, fontSize: '0.82rem', color: '#fff', letterSpacing: '0.03em' }}>
                {title.replace(/^ESP for\s*/i, '').toUpperCase()}
              </div>
              <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'rgba(139,156,196,0.9)', letterSpacing: '0.08em', marginTop: '1px' }}>
                {level}
              </div>
            </div>
          )}
        </div>

        {/* ── MIDDLE: Metadata + Stats ──────────────────────────────────── */}
        <div style={{
          width:         isMobile ? '100%' : '280px',
          flexShrink:    0,
          padding:       isMobile ? '20px 20px 4px' : '22px 20px 20px 22px',
          display:       'flex',
          flexDirection: 'column',
          gap:           '9px',
          borderRight:   isMobile ? 'none' : `1px solid ${C.border}`,
          borderBottom:  isMobile ? `1px solid ${C.border}` : 'none',
        }}>
          {/* Title row + tier pill */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
            <h3 style={{
              fontSize:      'clamp(0.95rem, 1.8vw, 1.08rem)',
              fontWeight:    800,
              color:         C.text,
              margin:        0,
              lineHeight:    1.25,
              letterSpacing: '-0.01em',
              flex:          1,
              minWidth:      0,
            }}>
              {title}
            </h3>
            <span style={{
              flexShrink:    0,
              padding:       '3px 9px',
              borderRadius:  '999px',
              fontSize:      '0.56rem',
              fontWeight:    800,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              alignSelf:     'flex-start',
              background:    tierBg,
              border:        `1px solid ${tierBorder}`,
              color:         tierColor,
            }}>
              {tierLabel}
            </span>
          </div>

          {/* Description */}
          <p style={{
            fontSize:   '0.8rem',
            color:      C.muted,
            margin:     0,
            lineHeight: 1.6,
          }}>
            {description}
          </p>

          {/* Level pill */}
          {level && (
            <div style={{ alignSelf: 'flex-start' }}>
              <span style={{
                display:       'inline-flex',
                alignItems:    'center',
                gap:           '5px',
                padding:       '3px 10px',
                borderRadius:  '999px',
                background:    'rgba(79,140,247,0.08)',
                border:        '1px solid rgba(79,140,247,0.20)',
                fontSize:      '0.63rem',
                fontWeight:    700,
                letterSpacing: '0.07em',
                color:         C.accent,
              }}>
                <IconLevel size={10} color={C.accent} />
                {level} Level
              </span>
            </div>
          )}

          {/* Divider */}
          <div style={{ height: '1px', background: C.border, margin: '1px 0' }} />

          {/* 4-stat grid */}
          <div style={{
            display:             'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap:                 '10px 8px',
          }}>
            {([
              { value: String(unitCount),                          label: 'Units'        },
              { value: `${overallPct}%`,                           label: 'Completed'    },
              { value: lastMs ? formatActivity(lastMs) : 'Not started', label: 'Last activity'},
              { value: 'Online & Offline',                         label: 'Availability' },
            ] as const).map(({ value, label }) => (
              <div key={label}>
                <div style={{
                  fontWeight:  800,
                  fontSize:    label === 'Availability' ? '0.7rem' : '1rem',
                  color:       C.text,
                  lineHeight:  1.2,
                  whiteSpace:  'nowrap',
                }}>
                  {value}
                </div>
                <div style={{
                  fontSize:      '0.6rem',
                  color:         C.muted,
                  fontWeight:    600,
                  marginTop:     '2px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Unit strip ─────────────────────────────────────────── */}
        <div style={{
          flex:      1,
          minWidth:  0,
          display:   'flex',
          alignItems:'center',
          position:  'relative',
          padding:   isMobile ? '16px 0 20px' : '0',
        }}>
          {/* Left scroll arrow — desktop, 5+ units */}
          {!isMobile && unitCount > 5 && (
            <button
              style={{
                flexShrink:     0,
                marginLeft:     '8px',
                width:          '32px',
                height:         '32px',
                borderRadius:   '50%',
                background:     'rgba(79,140,247,0.08)',
                border:         '1px solid rgba(79,140,247,0.22)',
                color:          C.accent,
                fontSize:       '18px',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                cursor:         'pointer',
                fontFamily:     'inherit',
                transition:     'background 0.2s ease, transform 0.2s ease',
              }}
              onClick={() => scrollUnits('left')}
              aria-label="Show previous units"
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(79,140,247,0.16)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(79,140,247,0.08)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
              }}
            >
              ‹
            </button>
          )}

          {/* Scrollable tiles */}
          <style>{`.bc-strip-${bookId.replace(/[^a-z0-9]/gi, '_')}::-webkit-scrollbar { display:none; }`}</style>
          <div
            ref={scrollRef}
            className={`bc-strip-${bookId.replace(/[^a-z0-9]/gi, '_')}`}
            style={{
              display:                'flex',
              gap:                    '8px',
              overflowX:              'auto',
              scrollbarWidth:         'none',
              msOverflowStyle:        'none',
              WebkitOverflowScrolling:'touch',
              padding:                isMobile ? '4px 20px 4px' : '16px 8px 16px 8px',
              scrollSnapType:         isMobile ? 'x mandatory' : undefined,
              flex:                   1,
            } as React.CSSProperties}
          >
            {units.map(({ n, unitId, pct, lockState }) => (
              <div
                key={n}
                style={isMobile ? { scrollSnapAlign: 'start' } : {}}
              >
                <UnitTile
                  bookId={bookId}
                  unitNumber={n}
                  totalUnits={unitCount}
                  unitTitle={unitTitles?.[n - 1]}
                  percent={pct}
                  lockState={lockState}
                  isActive={n === activeN && lockState === 'open'}
                  isLoading={loadingId === unitId}
                  onStart={() => void handleStart(unitId)}
                  onStartTest={(version) => void handleStartTest(n, lockState, version)}
                  unitProgress={progressData[`${bookId}_unit${n}`]}
                />
              </div>
            ))}
          </div>

          {/* Right scroll arrow — desktop, 5+ units */}
          {!isMobile && unitCount > 5 && (
            <button
              style={{
                flexShrink:     0,
                marginRight:    '12px',
                width:          '32px',
                height:         '32px',
                borderRadius:   '50%',
                background:     'rgba(79,140,247,0.08)',
                border:         '1px solid rgba(79,140,247,0.22)',
                color:          C.accent,
                fontSize:       '18px',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                cursor:         'pointer',
                fontFamily:     'inherit',
                transition:     'background 0.2s ease, transform 0.2s ease',
              }}
              onClick={() => scrollUnits('right')}
              aria-label="Show more units"
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(79,140,247,0.16)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(79,140,247,0.08)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
              }}
            >
              ›
            </button>
          )}
        </div>

      </div>
    </div>
  );
}